import { useState, useEffect, useRef } from "react";
import { Play, X, Headphones } from "lucide-react";

/**
 * CustomAudioPlayer — premium single-screen audio player
 * Strict download protection: no native controls, no picture-in-picture,
 * no right-click context menu, nodownload attributes.
 */
const CustomAudioPlayer = ({ src, title, onClose, testId, nowPlayingLabel }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration || 0);
    const onEnded = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.play().catch(() => {});
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.pause();
    };
  }, [src]);

  const toggle = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play(); else audio.pause();
  };

  const seek = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const audio = audioRef.current;
    if (audio && duration) audio.currentTime = pct * duration;
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const pct = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-5" data-testid={testId} onContextMenu={(e) => e.preventDefault()}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        style={{ display: 'none' }}
      />
      {/* Identity */}
      <div className="flex items-center gap-3 shrink-0">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.3)' }}>
          <Headphones className="w-4 h-4 text-blue-400/80" />
        </div>
        <div className="min-w-0">
          <p className="text-blue-400/75 text-[9px] tracking-[0.28em] uppercase font-bold leading-none">{nowPlayingLabel}</p>
          <p className="text-white/75 text-[12px] font-medium leading-tight mt-1 truncate max-w-[220px]">{title}</p>
        </div>
      </div>
      {/* Play / Pause */}
      <button
        onClick={toggle}
        className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-105 shrink-0"
        style={{ background: 'rgba(96,165,250,0.18)', border: '1px solid rgba(96,165,250,0.4)', boxShadow: isPlaying ? '0 0 16px rgba(96,165,250,0.35)' : 'none' }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <div className="flex gap-[3px]">
            <div className="w-[3px] h-3.5 rounded-sm bg-blue-400/90" />
            <div className="w-[3px] h-3.5 rounded-sm bg-blue-400/90" />
          </div>
        ) : (
          <Play className="w-4 h-4 text-blue-400/90 ml-0.5" fill="currentColor" />
        )}
      </button>
      {/* Progress + time */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        <span className="text-white/55 text-[11px] tabular-nums shrink-0 w-10 text-right">{fmt(currentTime)}</span>
        <div className="flex-1 h-[6px] rounded-full relative cursor-pointer group" onClick={seek} style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-150" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, rgba(96,165,250,0.95), rgba(96,165,250,0.6))' }} />
          <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ left: `calc(${pct}% - 6px)`, background: '#60A5FA', boxShadow: '0 0 10px rgba(96,165,250,0.6)' }} />
        </div>
        <span className="text-white/40 text-[11px] tabular-nums shrink-0 w-10">{fmt(duration)}</span>
      </div>
      {/* Close */}
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 shrink-0" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }} aria-label="Close">
        <X className="w-3.5 h-3.5 text-white/60" />
      </button>
    </div>
  );
};

export default CustomAudioPlayer;
